import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Bug,
  CheckCircle,
  Clock,
  AlertCircle,
  Loader2,
  Search,
  Play,
  XCircle,
  History,
  Rocket,
  Plus,
  MessageSquare,
  Calendar,
  User,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';
import { APP_VERSION, VERSION_HISTORY, BACKLOG, getChangeTypeColor, getChangeTypeLabel } from '../../config/version';

const RoadmapPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('changelog');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [bugsTodo, setBugsTodo] = useState([]);
  const [bugsResolved, setBugsResolved] = useState([]);
  const [bugStats, setBugStats] = useState({ total: 0, new: 0, in_progress: 0, resolved: 0 });
  
  // Dialog states
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [selectedBug, setSelectedBug] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [versionFixed, setVersionFixed] = useState(APP_VERSION.version);
  
  // Filter
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [todoRes, allRes, statsRes] = await Promise.all([
        api.get('/bugs/todo'),
        api.get('/bugs/'),
        api.get('/bugs/stats/summary').catch(() => ({ data: { total: 0, new: 0, in_progress: 0, resolved: 0 } }))
      ]);
      
      setBugsTodo(todoRes.data || []);
      setBugsResolved((allRes.data || []).filter(b => b.status === 'resolved'));
      setBugStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartProgress = async (bugId) => {
    try {
      await api.put(`/bugs/${bugId}/status?status=in_progress`);
      toast.success('Bug marqué en cours de traitement');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleOpenResolve = (bug) => {
    setSelectedBug(bug);
    setResolutionNotes('');
    setVersionFixed(APP_VERSION.version);
    setShowResolveDialog(true);
  };

  const handleResolve = async () => {
    if (!selectedBug) return;
    
    try {
      await api.put(`/bugs/${selectedBug.id}/status?status=resolved&resolution_notes=${encodeURIComponent(resolutionNotes)}&version_fixed=${versionFixed}`);
      toast.success('Bug marqué comme résolu ! Notification envoyée.');
      setShowResolveDialog(false);
      setSelectedBug(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la résolution');
    }
  };

  const filteredTodo = bugsTodo.filter(bug =>
    bug.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    bug.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const priorityColors = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-slate-100 text-slate-700 border-slate-200'
  };

  const priorityLabels = {
    critical: 'Critique',
    high: 'Haute',
    medium: 'Moyenne',
    low: 'Basse'
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3" style={{ color: theme.colors?.primary }}>
              <Rocket className="w-8 h-8" />
              Roadmap & Bugs
            </h1>
            <p className="text-[#64748b] mt-1">
              Gérez les bugs signalés et les évolutions de l&apos;application
            </p>
          </div>
          <Badge className="text-sm px-3 py-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-0">
            Version {APP_VERSION.version}
          </Badge>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Bug className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold" style={{ color: theme.colors?.primary }}>{bugStats.total}</p>
                <p className="text-xs text-[#64748b]">Total bugs</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <AlertCircle className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-red-600">{bugStats.new}</p>
                <p className="text-xs text-[#64748b]">Nouveaux</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-amber-600">{bugStats.in_progress}</p>
                <p className="text-xs text-[#64748b]">En cours</p>
              </div>
            </CardContent>
          </Card>

          <Card className="card">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-emerald-600">{bugStats.resolved}</p>
                <p className="text-xs text-[#64748b]">Résolus</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-4 w-full max-w-xl">
            <TabsTrigger value="changelog" className="gap-2" data-testid="tab-changelog">
              <History className="w-4 h-4" />
              Changelog
            </TabsTrigger>
            <TabsTrigger value="backlog" className="gap-2" data-testid="tab-backlog">
              <Rocket className="w-4 h-4" />
              Backlog
            </TabsTrigger>
            <TabsTrigger value="todo" className="gap-2" data-testid="tab-todo">
              <Bug className="w-4 h-4" />
              Bugs ({filteredTodo.length})
            </TabsTrigger>
            <TabsTrigger value="resolved" className="gap-2" data-testid="tab-resolved">
              <CheckCircle className="w-4 h-4" />
              Résolus
            </TabsTrigger>
          </TabsList>

          {/* TODO Tab */}
          <TabsContent value="todo" className="space-y-4">
            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]" />
              <Input 
                placeholder="Rechercher un bug..." 
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: theme.colors?.primary }} />
              </div>
            ) : filteredTodo.length === 0 ? (
              <Card className="card">
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
                  <h3 className="text-lg font-semibold mb-2">Aucun bug à traiter ! 🎉</h3>
                  <p className="text-[#64748b]">Tous les bugs signalés ont été résolus.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredTodo.map((bug) => (
                  <Card key={bug.id} className="card hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {/* Priority indicator */}
                        <div className={cn(
                          "w-1 h-full min-h-[80px] rounded-full",
                          bug.priority === 'critical' ? 'bg-red-500' :
                          bug.priority === 'high' ? 'bg-orange-500' :
                          bug.priority === 'medium' ? 'bg-amber-500' : 'bg-slate-300'
                        )} />
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <h4 className="font-semibold" style={{ color: theme.colors?.primary }}>
                                {bug.title}
                              </h4>
                              <p className="text-sm text-[#64748b] mt-1 line-clamp-2">
                                {bug.description}
                              </p>
                            </div>
                            
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge className={cn("text-xs", priorityColors[bug.priority])}>
                                {priorityLabels[bug.priority]}
                              </Badge>
                              <Badge className={cn(
                                "text-xs",
                                bug.status === 'new' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                              )}>
                                {bug.status === 'new' ? 'Nouveau' : 'En cours'}
                              </Badge>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-3 text-xs text-[#64748b]">
                              <div className="flex items-center gap-1">
                                <Avatar className="w-5 h-5">
                                  <AvatarImage src={bug.reporter_avatar} />
                                  <AvatarFallback className="text-[8px]">
                                    {bug.reporter_name?.charAt(0) || 'U'}
                                  </AvatarFallback>
                                </Avatar>
                                <span>{bug.reporter_name}</span>
                              </div>
                              <span>•</span>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(bug.created_at).toLocaleDateString('fr-FR')}
                              </div>
                              {bug.page_url && (
                                <>
                                  <span>•</span>
                                  <span className="truncate max-w-[150px]">{bug.page_url}</span>
                                </>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              {bug.status === 'new' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleStartProgress(bug.id)}
                                >
                                  <Play className="w-3 h-3 mr-1" />
                                  Démarrer
                                </Button>
                              )}
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleOpenResolve(bug)}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Résoudre
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Resolved Tab */}
          <TabsContent value="resolved" className="space-y-4">
            {bugsResolved.length === 0 ? (
              <Card className="card">
                <CardContent className="p-12 text-center text-[#64748b]">
                  Aucun bug résolu récemment
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {bugsResolved.slice(0, 20).map((bug) => (
                  <Card key={bug.id} className="card">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-emerald-500" />
                          <div>
                            <h4 className="font-medium">{bug.title}</h4>
                            <p className="text-xs text-[#64748b]">
                              Résolu le {new Date(bug.resolved_at).toLocaleDateString('fr-FR')}
                              {bug.version_fixed && ` • v${bug.version_fixed}`}
                            </p>
                          </div>
                        </div>
                        {bug.resolution_notes && (
                          <p className="text-sm text-[#64748b] max-w-md truncate">
                            {bug.resolution_notes}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Changelog Tab */}
          <TabsContent value="changelog" className="space-y-4" data-testid="changelog-content">
            {/* Current Version */}
            <Card className="card border-l-4 border-l-indigo-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-500" />
                  Version actuelle : {APP_VERSION.version}
                </CardTitle>
                <CardDescription>
                  Publiée le {new Date(APP_VERSION.buildDate).toLocaleDateString('fr-FR')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {APP_VERSION.changes.map((change, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Badge className={cn("text-[10px] mt-0.5 flex-shrink-0", getChangeTypeColor(change.type))}>
                        {getChangeTypeLabel(change.type)}
                      </Badge>
                      <span>{change.description}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Version History */}
            <h3 className="font-semibold text-lg mt-6" style={{ color: theme.colors?.primary }}>
              Historique des versions ({VERSION_HISTORY.length})
            </h3>
            <ScrollArea className="h-[500px]">
              <div className="space-y-4 pr-4">
                {VERSION_HISTORY.map((version, idx) => (
                  <Card key={idx} className="card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <History className="w-4 h-4" />
                        Version {version.version}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        {new Date(version.date).toLocaleDateString('fr-FR')}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-1">
                        {version.changes.map((change, cIdx) => (
                          <li key={cIdx} className="flex items-start gap-2 text-sm text-[#64748b]">
                            <Badge className={cn("text-[10px] mt-0.5 flex-shrink-0", getChangeTypeColor(change.type))}>
                              {getChangeTypeLabel(change.type)}
                            </Badge>
                            <span>{change.description}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Backlog Tab */}
          <TabsContent value="backlog" className="space-y-4" data-testid="backlog-content">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg" style={{ color: theme.colors?.primary }}>
                Fonctionnalités planifiées ({BACKLOG.length})
              </h3>
              <div className="flex items-center gap-2 text-sm text-[#64748b]">
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-full"></span> P0</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-orange-500 rounded-full"></span> P1</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-amber-500 rounded-full"></span> P2</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-slate-400 rounded-full"></span> P3</span>
              </div>
            </div>
            
            <div className="space-y-3">
              {BACKLOG.map((item, idx) => (
                <Card key={idx} className={cn(
                  "card border-l-4",
                  item.priority === 'P0' ? 'border-l-red-500' :
                  item.priority === 'P1' ? 'border-l-orange-500' :
                  item.priority === 'P2' ? 'border-l-amber-500' : 'border-l-slate-300'
                )}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={cn(
                            "text-xs",
                            item.priority === 'P0' ? 'bg-red-100 text-red-700 border-red-200' :
                            item.priority === 'P1' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                            item.priority === 'P2' ? 'bg-amber-100 text-amber-700 border-amber-200' : 
                            'bg-slate-100 text-slate-700 border-slate-200'
                          )}>
                            {item.priority}
                          </Badge>
                          <Badge className={cn(
                            "text-xs",
                            item.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            item.status === 'planned' ? 'bg-purple-100 text-purple-700' :
                            'bg-slate-100 text-slate-700'
                          )}>
                            {item.status === 'in_progress' ? 'En cours' : 
                             item.status === 'planned' ? 'Planifié' : item.status}
                          </Badge>
                        </div>
                        <h4 className="font-semibold" style={{ color: theme.colors?.primary }}>
                          {item.title}
                        </h4>
                        <p className="text-sm text-[#64748b] mt-1">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {BACKLOG.length === 0 && (
              <Card className="card">
                <CardContent className="p-12 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
                  <h3 className="text-lg font-semibold mb-2">Backlog vide ! 🎉</h3>
                  <p className="text-[#64748b]">Toutes les fonctionnalités prévues ont été implémentées.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Resolve Dialog */}
        <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Marquer comme résolu</DialogTitle>
            </DialogHeader>
            
            {selectedBug && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <h4 className="font-medium">{selectedBug.title}</h4>
                  <p className="text-sm text-[#64748b] mt-1">{selectedBug.description}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Version de correction</Label>
                  <Input 
                    value={versionFixed}
                    onChange={(e) => setVersionFixed(e.target.value)}
                    placeholder="ex: 1.6.2"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Notes de résolution (visible par tous)</Label>
                  <Textarea 
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder="Décrivez la correction apportée..."
                    rows={3}
                  />
                </div>
                
                <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                  <strong>ℹ️ Note :</strong> Une notification sera envoyée à la personne qui a signalé ce bug, 
                  et une annonce sera affichée dans la bannière pour informer tous les membres.
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResolveDialog(false)}>
                Annuler
              </Button>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleResolve}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirmer la résolution
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default RoadmapPage;
